/*
  Flower Box Sale: garden settings (bloom-config.js)

  This is the one file to edit for flower colors, flower images and meadow colors.
  You do not need to know how to code to change it. Follow these rules:

  1. To change a color, edit the hex code between the quotes, for example
     hex: '#E99A8C'  becomes  hex: '#E58F80'. Keep the # and the quotes.
  2. alpha is how see-through a shade is: 1 is solid, 0.5 is half see-through.
     Leave it out (or set it to 1) for solid shades.
  3. To add a shade, copy one whole line that starts with { name: and paste it
     below, then change the name and hex. Keep the comma at the end of the line.
  4. To use a painted or photographed flower instead of the drawn one, upload a
     transparent PNG or WebP to Shopify (Content > Files), copy its link and paste
     it between the quotes of image: ''. Leave it blank ('') to keep the drawn flower.
  5. Box keys (the names in quotes such as 'sculpt-studio') are permanent. Do not
     rename them; the theme section, the live count server and this file all share them.
  6. Every flower uses the shade list in order: flower 1 uses the first shade,
     flower 2 the second, and so on, starting over at the end of the list. So the
     meadow looks the same for every visitor.

  Colors: sampled from Wildflowers product photos on 2026-10-04. reference/colors-table.md
  lists every source photo. Shades marked Low confidence keep a placeholder until someone
  checks them against the real product.

  After editing, save the file in the Shopify code editor. The garden picks it up
  on the next page load.
*/
(function () {
  'use strict';

  window.BloomConfig = {
    version: 1,

    // Placement seed. Changing it reshuffles where every flower stands. Leave as is.
    seed: 20261013,

    boxes: {
      // Sculpt Studio Box: tulips.
      'sculpt-studio': {
        title: 'Sculpt Studio Box',
        species: 'tulip',
        handles: ['sculpt-studio-box'], // real store handle (checked 2026-10-06)
        max: 188, // most boxes that could ever be packed (used for test fills only)
        glitter: false,
        scale: 1.0, // 1 is normal size; 1.1 is 10 percent bigger
        image: '',
        colors: [
          { name: 'Clear Pink', hex: '#F0B9C4', alpha: 0.82 },
          // Low confidence: photo sample read #E2A9DD (from a label or small sample); check against the real product
          { name: 'Sheer White', hex: '#F3E9E4', alpha: 0.8 },
          // Low confidence: photo sample read #F3E9E4 (from a label or small sample); check against the real product
          { name: 'Warm Pink', hex: '#E99A8C' },
          // Low confidence: photo sample read #CF9796 (from a label or small sample); check against the real product
          { name: 'White', hex: '#FBF8F3' }
          // Low confidence: photo sample read #E6F3F8 (from a label or small sample); check against the real product
        ]
      },

      // Sculpt Sampler: daffodils.
      'sculpt-sampler': {
        title: 'Sculpt Sampler',
        species: 'daffodil',
        handles: ['sculpt-sampler'], // real store handle (checked 2026-10-06)
        max: 235,
        glitter: false,
        scale: 1.0,
        image: '',
        colors: [
          { name: 'Clear Pink', hex: '#F0B9C4', alpha: 0.82 },
          // Low confidence: photo sample read #E2A9DD (from a label or small sample); check against the real product
          { name: 'Sheer White', hex: '#F3E9E4', alpha: 0.8 },
          // Low confidence: photo sample read #F3E9E4 (from a label or small sample); check against the real product
          { name: 'Warm Pink', hex: '#E99A8C' },
          // Low confidence: photo sample read #CF9796 (from a label or small sample); check against the real product
          { name: 'White', hex: '#FBF8F3' }
          // Low confidence: photo sample read #E6F3F8 (from a label or small sample); check against the real product
        ]
      },

      // Glitter Gel Vault: cosmos with a sparkle that is always on.
      // Glitter swatches average darker than the gel looks in the jar (dark flecks pull the
      // average down), so Lauren may want to lighten these. The flower drawing brightens
      // petal tips so even the deeper shades still read as flowers.
      'glitter-vault': {
        title: 'Glitter Gel Vault',
        species: 'cosmos',
        handles: ['glitter-gel-vault'], // real store handle (checked 2026-10-06)
        max: 205,
        glitter: true,
        scale: 1.0,
        image: '',
        colors: [
          { name: 'Pink Glitter', hex: '#F2A7C3' },
          // Low confidence: photo sample read #8C595E (glitter swatch averages darker than the jar); Lauren to confirm against the real product
          { name: 'Pink Shimmer', hex: '#D7867E' },
          { name: 'Pink Sparkle', hex: '#BF8894' },
          { name: 'Gold Glitter', hex: '#D9B25C' },
          // Low confidence: photo sample read #745C42 (glitter swatch averages darker than the jar); Lauren to confirm against the real product
          { name: 'Silver Glitter', hex: '#C9CDD2' },
          // Low confidence: photo sample read #707785 (from a label or small sample); check against the real product
          { name: 'Purple Glitter', hex: '#545E8A' },
          { name: 'Blue Glitter', hex: '#2D7D8C' },
          { name: 'Iridescent Glitter', hex: '#DCEBF2' },
          // Low confidence: photo sample read #B2C2C9 (from a label or small sample); check against the real product
          { name: 'Clear Sparkle', hex: '#F4F4F2', alpha: 0.7 }
          // Low confidence: photo sample read #A6A9A1 (from a label or small sample); check against the real product
        ]
      },

      // Art Paste Trio: poppies.
      'art-paste-trio': {
        title: 'Art Paste Trio',
        species: 'poppy',
        handles: ['art-paste-trio'], // real store handle (checked 2026-10-06)
        max: 208,
        glitter: false,
        scale: 1.0,
        image: '',
        colors: [
          { name: 'Whisper', hex: '#FE8DC1' },
          { name: 'Neon', hex: '#FE4F6E' },
          { name: 'Fall', hex: '#FC5445' }
        ]
      },

      // Ink Library: irises.
      'ink-library': {
        title: 'Ink Library',
        species: 'iris',
        handles: ['ink-library'], // real store handle (checked 2026-10-06)
        max: 304,
        glitter: false,
        scale: 1.0,
        image: '',
        colors: [
          { name: 'Ink 1 (lime green)', hex: '#B9DB7E' },
          { name: 'Ink 2 (magenta)', hex: '#C94FC5' },
          { name: 'Ink 3 (violet)', hex: '#8E52D3' },
          { name: 'Ink 4 (sienna tan)', hex: '#A07952' },
          { name: 'Ink 5 (smoke gray)', hex: '#8B93A1' },
          { name: 'Ink 6 (cobalt blue)', hex: '#016AB9' },
          { name: 'Ink 7 (coral)', hex: '#CE6C68' },
          { name: 'Ink 8 (emerald)', hex: '#2E986F' },
          { name: 'Ink 9 (golden yellow)', hex: '#FED446' }
        ]
      },

      // Builder Color Box: red roses (three reds so neighbours differ).
      'builder-color': {
        title: 'Builder Color Box',
        species: 'rose',
        handles: ['builder-color-box'], // real store handle (checked 2026-10-06)
        max: 89,
        glitter: false,
        scale: 1.0,
        image: '',
        colors: [
          { name: 'Rose red', hex: '#D7263D' },
          { name: 'Crimson', hex: '#B3122E' },
          { name: 'Scarlet', hex: '#E8433F' }
        ]
      },

      // Puffy Gel Set: zinnias, one rainbow color per flower.
      'puffy-gel': {
        title: 'Puffy Gel Set',
        species: 'zinnia',
        handles: ['puffy-gel'], // real store handle (checked 2026-10-06)
        max: 226,
        glitter: false,
        scale: 1.0,
        image: '',
        colors: [
          { name: 'Puffy 01 (orchid pink)', hex: '#E57CD4' },
          { name: 'Puffy 02 (lilac)', hex: '#B7A4FD' },
          // Low confidence: label-derived sample; the placeholder was #F4845F. Check against the real product
          { name: 'Puffy 03 (sky blue)', hex: '#5FD6FE' },
          // Low confidence: label-derived sample; the placeholder was #F9C74F. Check against the real product
          { name: 'Puffy 04 (pink)', hex: '#FBBFF5' },
          // Low confidence: label-derived sample; the placeholder was #90BE6D. Check against the real product
          { name: 'Puffy 05 (yellow)', hex: '#F8F877' },
          // Low confidence: label-derived sample; the placeholder was #43AA8B. Check against the real product
          { name: 'Puffy 06 (green)', hex: '#62CE55' },
          { name: 'Puffy 07 (mint turquoise)', hex: '#93E5D9' },
          { name: 'Puffy 08 (black)', hex: '#1B2124' },
          { name: 'Puffy 09 (white)', hex: '#FDFDFD' },
          // Low confidence: label-derived sample; the placeholder was #9B5DE5. Check against the real product
          { name: 'Puffy 10 (gold)', hex: '#CDAE3E' },
          // Low confidence: label-derived sample; the placeholder was #F15BB5. Check against the real product
          { name: 'Puffy 11 (silver)', hex: '#AEB1AE' },
          // Low confidence: label-derived sample; the placeholder was #FEE440. Check against the real product
          { name: 'Puffy 12 (red)', hex: '#EE3C30' },
          // Low confidence: label-derived sample; the placeholder was #00BBF9. Check against the real product
        ]
      }
    },

    // Species-wide images. Used when a box has no image of its own. Blank = drawn flower.
    species: {
      'tulip': { image: '' },
      'daisy': { image: '' },
      'daffodil': { image: '' },
      'cosmos': { image: '' },
      'poppy': { image: '' },
      'iris': { image: '' },
      'rose': { image: '' },
      'zinnia': { image: '' }
    },

    // Replaced flowers. A product whose Flower species field still says one of the names on the
    // left shows the flower on the right, so nothing in the store needs retyping.
    speciesAliases: {
      'peony': 'tulip',
      'daisy': 'tulip',
      'peony-bud': 'daffodil',
      'garden-rose': 'rose'
    },

    // The flower names shoppers read, with a capital letter (as in "Tulip 31, planted by a Sculpt Studio Box").
    speciesNames: { tulip: 'Tulip', daffodil: 'Daffodil', cosmos: 'Cosmos', poppy: 'Poppy', iris: 'Iris', rose: 'Rose', zinnia: 'Zinnia' },

    // The same names for two or more flowers (as in "Your 2 tulips are growing in the meadow.").
    speciesPlural: { tulip: 'tulips', daffodil: 'daffodils', cosmos: 'cosmos', poppy: 'poppies', iris: 'irises', rose: 'roses', zinnia: 'zinnias' },

    // Meadow colors for each phase. The theme editor colors (Sky, Soil, Full Bloom light)
    // override skyTop, soil and light when they are set in the section. The scene, back to
    // front: sky, far mountains and a cliff, the glowing emerald city, rolling hills, the ground
    // and the yellow brick road. rainbow is how strong the rainbow is (0 hides it).
    // rock is no longer used (the boulders were removed); it can stay or go.
    meadow: {
      // Seeds: twilight over the sown field, the city already lit.
      tease: {
        skyTop: '#2F2C6E', skyMid: '#6E5A9F', skyBottom: '#E8B39A', light: '#FFD5A8',
        mountains: '#5A5486', cliff: '#6B6C8A', hills: '#4F7A55', hillsFar: '#6E8A77', rock: '#7E7A8C',
        soil: '#4E3A2C', soilHighlight: '#6E5340',
        grassBack: '#6E8F62', grassMid: '#4C7A4B', grassFront: '#2F5533',
        road: '#C9A24A', roadDeep: '#8E6A26', roadLine: '#5A3C12',
        city: '#2F9A6A', cityLight: '#B6FFD9', cityGlow: '#8DF0B8',
        haze: '#B9A2B8', tint: '', tintAmount: 0, rainbow: 0, stars: true
      },
      // Bloom: a bright blue day.
      bloom: {
        skyTop: '#5F6FC9', skyMid: '#9DAEEA', skyBottom: '#EAEEF8', light: '#FFF8EC',
        mountains: '#8B93BE', cliff: '#9AA1B3', hills: '#7FB478', hillsFar: '#A9C9A0', rock: '#A7ACB5',
        soil: '#5A4636', soilHighlight: '#7A5F4A',
        grassBack: '#8FC27E', grassMid: '#4F9F55', grassFront: '#2F7A3E',
        road: '#E7B93A', roadDeep: '#C78E22', roadLine: '#8A5A14',
        city: '#2E9E6A', cityLight: '#9FF2C8', cityGlow: '#8DF0B8',
        haze: '#DCE4F3', tint: '', tintAmount: 0, rainbow: 0.17, stars: false
      },
      // Full Bloom: golden hour.
      full: {
        skyTop: '#C58FC2', skyMid: '#F3B98B', skyBottom: '#FBE4B8', light: '#FFD98A',
        mountains: '#B08AA0', cliff: '#B49C9C', hills: '#9AA86A', hillsFar: '#C9B990', rock: '#B9A48E',
        soil: '#5A4636', soilHighlight: '#7A5F4A',
        grassBack: '#B8B079', grassMid: '#7E9A55', grassFront: '#46653A',
        road: '#F0C34A', roadDeep: '#C98A22', roadLine: '#8A5A14',
        city: '#3FA574', cityLight: '#F8F0B0', cityGlow: '#FFE39A',
        haze: '#F2CFA6', tint: '#F5C97A', tintAmount: 0.14, rainbow: 0.12, stars: false
      },
      // Pressed: the finished meadow on soft paper.
      pressed: {
        skyTop: '#D9DBE6', skyMid: '#E6E6EC', skyBottom: '#F4EFE6', light: '#FBF8F2',
        mountains: '#BDBEC9', cliff: '#C4C3C7', hills: '#B7C2A6', hillsFar: '#D0D4C3', rock: '#BDBCB6',
        soil: '#6B5A4A', soilHighlight: '#8A7663',
        grassBack: '#BDC2A6', grassMid: '#8EA27C', grassFront: '#5E7552',
        road: '#DCC48C', roadDeep: '#B89C62', roadLine: '#8C7A58',
        city: '#86A890', cityLight: '#E0EEE4', cityGlow: '#D7E8DB',
        haze: '#F4EFE6', tint: '#E6DCC6', tintAmount: 0.24, rainbow: 0.06, stars: false
      }
    },

    // Words the garden shows. Anything in {curly brackets} is filled in automatically, so keep
    // those exactly as they are. "a" in front of {box} or {species} turns into "an" by itself
    // when the next word starts with a vowel (an Ink Library, an iris).
    labels: {
      plantedBy: '{species} {number}, planted by a {box}', // tap or hover a flower: Tulip 31, planted by a Sculpt Studio Box
      plantedByNamed: "{name}'s {species}, planted by a {box}", // the same for a flower its shopper named: Salon Luna's tulip, planted by a Sculpt Studio Box
      countOne: 'flower planted', // under the big number when it is 1
      countMany: 'flowers planted', // under the big number for every other count
      flowerOne: 'flower', // used in the Pressed thank-you line
      flowerMany: 'flowers', // the same line for more than one flower
      milestoneReached: 'Milestone reached: {count} boxes. {reward}', // read aloud to screen readers when a reward unlocks
      milestoneItem: '{count} boxes', // the label on each reward chip
      milestoneDone: 'Reached', // read to screen readers next to a reward that is unlocked
      recent: 'Just planted: a {species} for a {box}, {ago}.', // the quiet line under the count while the sale is live
      agoNow: 'just now', // under a minute ago
      agoMinute: '1 minute ago', // one minute ago
      agoMinutes: '{n} minutes ago', // 2 to 59 minutes ago
      agoHour: '1 hour ago', // one hour ago
      agoHours: '{n} hours ago', // 2 to 23 hours ago
      agoDay: 'yesterday', // 24 to 48 hours ago
      agoDays: '{n} days ago', // 2 days or more
      mineTitleOne: 'Your {flowers} is growing in the meadow.', // See your flower card, one flower: Your tulip is growing in the meadow.
      mineTitleMany: 'Your {flowers} are growing in the meadow.', // two or more: Your 2 tulips are growing in the meadow.
      minePressedOne: 'Your {flowers} is pressed in the meadow.', // the same card after the sale, one flower
      minePressedMany: 'Your {flowers} are pressed in the meadow.', // the same card after the sale, two or more
      mineLine: 'Flower {number} of the {box}, planted {date}.', // one line per box: Flower 31 of the Sculpt Studio Box, planted October 14.
      mineLineTwo: 'Flowers {first} and {last} of the {box}, planted {date}.', // two flowers from one box
      mineLineMany: 'Flowers {first} to {last} of the {box}, planted {date}.', // three or more flowers from one box
      minePinned: 'Your {flowers}', // the little tag above the shopper's own flower: Your tulip
      mineFallbackTitleOne: 'Your {flowers} is in this meadow.', // card when the order cannot be looked up yet, one kind of flower
      mineFallbackTitleMany: 'Your {flowers} are in this meadow.', // the same, more than one kind
      mineFallbackLine: 'It is one of the {count} planted by the {box} so far.', // one line per box in that card
      mineCopied: 'Link copied', // shown for a few seconds after Share copies the page link
      shareCaption: 'My {species} in the Flower Box Sale', // printed on the shared picture
      shareCaptionNamed: "{name}'s {species} in the Flower Box Sale", // the shared picture of a named flower: Salon Luna's tulip in the Flower Box Sale
      shareSite: 'wildflowersnailshop.com/pages/flower-box-sale', // printed under the caption on the shared picture
      nameSaved: 'Saved. Your flower is named {name}.', // card note after Save name (no second period when the name ends in one)
      nameRemoved: 'Name removed.', // card note after saving an empty name
      nameRejected: "That name can't be shown here. Try another.", // card note when the name filter says no
      nameTooLong: 'Keep it to 24 characters.', // card note when the name is longer than 24 characters
      nameFailed: "We couldn't save that right now. Try again in a moment." // card note when the save fails (offline, busy or not found)
    }
  };
})();
